import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import {
  COMPARE_DIFFERENCE_VISUALISATION_MAPPING,
  COMPARE_MESSAGE_TO_ICON_NAME,
  COMPARE_MESSAGES,
  COMPARE_TYPE_TO_MESSAGE,
  COMPARETYPES,
  MEALTYPE_TRANSLATION_KEYS,
  MEMBERSHIPTYPE_TRANSLATION_KEYS,
  PLATFORM_TRANSLATION_KEYS,
  RATE_PARAMS,
  RATE_PARAMS_TO_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import formatDate from 'lumen/utils/format-local-date';
import { isDateCompare } from 'lumen/utils/is-comparing-rates';

interface HotelAndMarketRatesTablesCellRateChangesSignature {
  Args: {
    customCss: unknown;
    params: unknown;
    rateObject: unknown;
  };
}

export default class HotelAndMarketRatesTablesCellRateChanges extends Component<HotelAndMarketRatesTablesCellRateChangesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare rates: Services['rates'];

  // Returns the translation for "Change" or "Difference"
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compareTextPlaceholder() {
    // @ts-expect-error: Incorrect type
    return this.args.params?.compareType === 'extract_date'
      ? this.intl.t('rates.changes.change_tooltip')
      : this.intl.t('general.difference');
  }

  /**
   * Returns an object describing the rate changes
   *
   * Table:
   * value: rate change displayed next to the base rate
   * highestPrioChangeIconName: contains the icon with the highest priority
   *
   * Tooltip:
   * currentSelection: e.g. Lowest
   * versusSelection: e.g. Best Flex
   * currentTooltipRate: the rate of to the current selection
   * versusTooltipRate: the rate of to the compare value
   * currentRateTypeString: e.g Refundable, Breakfast
   * versusRateTypeString: e.g Non Refundable, No breakfast
   * versusRoomName: the room name of the compare value
   *
   * Difference row:
   * showDifferenceRow: whether to show a row in the tooltip with the rate change details
   * class: css class for rateDifference
   * rateDifference: the absolute and relative difference between the rates
   * roomNameChangeText: explanation of the room change
   *
   * Warning row:
   * showWarningRow: boolean flag determining whether to show the warning row
   * extractDatetimeDifferenceString: time difference (in hours) wrapped in string
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateChangeValueObj() {
    // Check if there is a rate object with comparison rates (camelcase for market, snakecase for hotel)
    if (
      !(
        this.args.rateObject &&
        // @ts-expect-error: Incorrect type
        (this.args.rateObject?.comparisonRates ||
          // @ts-expect-error: Incorrect type
          this.args.rateObject?.comparison_rates)
      )
    ) {
      return '';
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const arrRateChanges =
      // @ts-expect-error: Incorrect type
      this.args.rateObject?.comparisonRates ||
      // @ts-expect-error: Incorrect type
      this.args.rateObject?.comparison_rates;
    // Check if ratechanges array contains an object for the selected rate change number
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (arrRateChanges.length === 1) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const compareType = this.args.params?.compareType;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const compareValue = this.args.params?.compareValue;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const ratechange = arrRateChanges[0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const compareRate = ratechange.value;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const compareMessage = ratechange.message;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const rateToday = this.args.rateObject?.value;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const messageToday = this.args.rateObject?.message;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const currentRoomName =
        // @ts-expect-error: Incorrect type
        this.args.rateObject?.room_name || this.args.rateObject?.roomName;
      const isCompareMissing = compareMessage === 'general.missing';

      const extractDatetimeDifference = Math.abs(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        dayjs(this.args.rateObject?.extract_datetime).diff(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          dayjs(ratechange.extract_datetime),
          'hour',
        ),
      );
      const extractDatetimeDifferenceString = this.intl.t(
        'rates.compare.warning.extract_datetime_difference',
        { extractDatetimeDifference },
      );
      // Only show the warning row if
      // 1. The compare is not missing
      // 2. The time difference is larger than one hour
      // 3. The user is not comparing to a past date
      const showWarningRow =
        !isCompareMissing &&
        extractDatetimeDifference > 1 &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        !isDateCompare(compareType);

      let currentSelection = '';
      let versusSelection = '';
      switch (compareType) {
        case COMPARETYPES.RATE_PARAM: {
          currentSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            RATE_PARAMS_TO_TRANSLATION_KEYS[this.args.params?.rateParam],
          );
          versusSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            RATE_PARAMS_TO_TRANSLATION_KEYS[compareValue],
          );
          break;
        }
        case COMPARETYPES.MEMBERSHIPTYPE: {
          currentSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            MEMBERSHIPTYPE_TRANSLATION_KEYS[this.args.params?.membershipType],
          );
          versusSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            MEMBERSHIPTYPE_TRANSLATION_KEYS[compareValue],
          );
          break;
        }
        case COMPARETYPES.MEALTYPE: {
          currentSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            MEALTYPE_TRANSLATION_KEYS[this.args.params?.mealPlan],
          );
          versusSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            MEALTYPE_TRANSLATION_KEYS[compareValue],
          );
          break;
        }
        case COMPARETYPES.LOS:
          // @ts-expect-error: Incorrect type
          currentSelection = `LOS${this.args.params?.los}`;
          versusSelection = `LOS${compareValue}`;
          break;
        case COMPARETYPES.PERSONS:
          currentSelection = this.intl.t('general.guest', {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            count: this.args.params?.selectedMaxPersons,
          });
          versusSelection = this.intl.t('general.guest', {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            count: compareValue,
          });
          break;
        case COMPARETYPES.PLATFORM:
          currentSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            PLATFORM_TRANSLATION_KEYS[this.args.params.platform],
          );
          versusSelection = this.intl.t(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            PLATFORM_TRANSLATION_KEYS[compareValue],
          );
          break;
        case COMPARETYPES.SWLY:
          currentSelection = this.intl.t('general.today');
          versusSelection = `${
            isCompareMissing
              ? this.intl.t('general.missing')
              : formatDate(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  this.hotelAndMarketService.toLocalTime(ratechange.from_date),
                )
          } (${this.intl.t('revenue_insight.swly_short')})`;
          break;
        case COMPARETYPES.ROOMTYPE: {
          // Roomtype mapping logic for custom roomtype names
          const roomTypeMapping =
            this.hotelAndMarketService.hotel?.roomtype?.roomtypeMapping;
          // @ts-expect-error: Incorrect type
          const currentRoomTypeObject = roomTypeMapping.find(
            // @ts-expect-error: Incorrect type
            (roomType) => roomType.roomtype_id === this.args.params?.roomType,
          );
          // @ts-expect-error: Incorrect type
          const versusRoomTypeObject = roomTypeMapping.find(
            (roomType) => roomType.roomtype_id === compareValue,
          );
          // @ts-expect-error: Incorrect type
          currentSelection = currentRoomTypeObject.roomtype_name;
          // @ts-expect-error: Incorrect type
          versusSelection = versusRoomTypeObject.roomtype_name;

          // Default room types need to be translated, custom room types don't
          // @ts-expect-error: Incorrect type
          if (!currentRoomTypeObject.roomtype_id.startsWith('c_')) {
            currentSelection = this.intl.t(
              // @ts-expect-error: Incorrect type
              `rates.roomtype.${currentRoomTypeObject.roomtype_name}`,
            );
          }
          // @ts-expect-error: Incorrect type
          if (!versusRoomTypeObject.roomtype_id.startsWith('c_')) {
            versusSelection = this.intl.t(
              // @ts-expect-error: Incorrect type
              `rates.roomtype.${versusRoomTypeObject.roomtype_name}`,
            );
          }

          break;
        }
        default:
          currentSelection = this.intl.t('general.today');
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          versusSelection = formatDate(dayjs(compareValue));
          break;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const differenceVisualisation =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        COMPARE_DIFFERENCE_VISUALISATION_MAPPING[compareType];

      if (typeof compareRate === 'number' && typeof rateToday === 'number') {
        const noMessage = compareMessage === 'rates.compare.unavailable';

        if (rateToday === 0 && compareRate > 0) {
          // if rateToday is sold out, and compareRate is a number
          // => display the old rate value between brackets
          return {
            value: htmlSafe(
              `[${this.hotelAndMarketService.formatRateChange(
                compareRate,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                ratechange.currency,
                {
                  // @ts-expect-error: Incorrect type
                  format: {},
                },
              )}]`,
            ),
            // Tooltip data
            currentSelection,
            versusSelection,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            currentTooltipRate: this.intl.t(messageToday),
            versusTooltipRate: this.hotelAndMarketService.formatRateChange(
              compareRate,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              ratechange.currency,
            ),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            versusRateTypeString: this.rates.getRateTypeString(ratechange),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            currentRoomName,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            versusRoomName: ratechange.room_name,
            // Difference row
            showDifferenceRow: false,
            // Warning row
            showWarningRow,
            extractDatetimeDifferenceString,
          };
        }

        if (
          (rateToday > 0 && compareRate === 0) ||
          (rateToday === compareRate &&
            compareRate === 0 &&
            messageToday !== compareMessage &&
            compareMessage !== undefined)
        ) {
          if (!noMessage) {
            let currentTooltipRate = '';

            // eslint-disable-next-line max-depth
            if (messageToday) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              currentTooltipRate = this.intl.t(messageToday);
            } else {
              // @ts-expect-error: Incorrect type
              currentTooltipRate = this.hotelAndMarketService.formatRateObject(
                // @ts-expect-error: Incorrect type
                this.args.rateObject,
              );
            }

            return {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              value: `[${this.intl.t(compareMessage)}]`,
              // Tooltip data
              currentSelection,
              versusSelection,
              currentTooltipRate,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              versusTooltipRate: this.intl.t(compareMessage),
              currentRateTypeString: this.rates.getRateTypeString(
                // @ts-expect-error: Incorrect type
                this.args.rateObject,
              ),
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              currentRoomName,
              versusRoomName: '',
              // Difference row
              showDifferenceRow: false,
              // Warning row
              showWarningRow,
              extractDatetimeDifferenceString,
            };
          }
        }

        if (!noMessage && Math.abs(rateToday - compareRate) >= 1) {
          const formattedAbsoluteDifference =
            this.hotelAndMarketService.formatRate(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              ratechange.difference_absolute,
              {
                includeCurrencySymbol: false,
                longFormat: false,
                includeSign: true,
              },
            );
          const formattedAbsoluteDifferenceWithSymbol =
            this.hotelAndMarketService.formatRateChange(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              ratechange.difference_absolute,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              ratechange.currency,
              { longFormat: false, includeSign: true },
            );

          let formattedRelativeDifference = null;
          const relativeDifferencePercent =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ratechange.difference_relative * 100;
          if (Math.abs(relativeDifferencePercent) > 1) {
            // Minus sign is already present, plus sign is not
            formattedRelativeDifference =
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              ratechange.difference_relative > 0 ? '+' : '';
            formattedRelativeDifference += `${Math.round(relativeDifferencePercent)}%`;
          } else {
            formattedRelativeDifference = '< 1%';
          }

          // @ts-expect-error: Incorrect type
          const ignoredCompareMessages = [];
          if (compareType === COMPARETYPES.RATE_PARAM) {
            const isComparingBestFlex =
              // @ts-expect-error: Incorrect type
              this.args.params.rateParam === RATE_PARAMS.BEST_FLEX ||
              compareValue === RATE_PARAMS.BEST_FLEX;
            const isComparingLowestFlex =
              // @ts-expect-error: Incorrect type
              this.args.params.rateParam === RATE_PARAMS.FLEXIBLE ||
              compareValue === RATE_PARAMS.FLEXIBLE;
            // eslint-disable-next-line max-depth
            if (isComparingBestFlex) {
              // Don't show Best Flex icon when comparing with Best Flex rates (best_flex is always different)
              ignoredCompareMessages.push(COMPARE_MESSAGES.BAR);
            }
            // eslint-disable-next-line max-depth
            if (isComparingLowestFlex || isComparingBestFlex) {
              // Don't show Lowest Flex icon when comparing Lowest Flex or Best Flex rates (cancellable is always different)
              ignoredCompareMessages.push(COMPARE_MESSAGES.CANCELLABLE);
            }
            // Notice there is no check for Third Party rates: we always want to show the rate_type change even when comparing
          } else {
            // Don't show Membership/Roomtype icon when comparing memberships/roomtypes rates
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ignoredCompareMessages.push(COMPARE_TYPE_TO_MESSAGE[compareType]);
          }

          let highestPrioChangeIconName = null;
          let hasRoomTypeChange = false;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const compareMessages = ratechange.compare_messages;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (compareMessages?.length) {
            // Filter out the redundant compare indicators (e.g. don't show roomtype change icon when comparing roomtypes)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const filteredCompareMessages = compareMessages.filter(
              // @ts-expect-error: Incorrect type
              (msg) => !ignoredCompareMessages.includes(msg),
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, max-depth
            if (filteredCompareMessages.length) {
              // Show the icon related to the first of the remaining messages
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              highestPrioChangeIconName =
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                COMPARE_MESSAGE_TO_ICON_NAME[filteredCompareMessages[0]];
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            hasRoomTypeChange = compareMessages.includes(
              COMPARE_MESSAGES.ROOMTYPE,
            );
          }

          const currentRateTypeString = this.rates.getRateTypeString(
            // @ts-expect-error: Incorrect type
            this.args.rateObject,
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const versusRateTypeString = this.rates.getRateTypeString(ratechange);

          return {
            value:
              differenceVisualisation === 'percentage'
                ? formattedRelativeDifference
                : formattedAbsoluteDifference,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            highestPrioChangeIconName,
            // Tooltip data
            currentSelection,
            versusSelection,
            currentTooltipRate: this.hotelAndMarketService.formatRateObject(
              // @ts-expect-error: Incorrect type
              this.args.rateObject,
            ),
            versusTooltipRate: this.hotelAndMarketService.formatRateChange(
              compareRate,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              ratechange.currency,
            ),
            currentRateTypeString,
            versusRateTypeString,
            hasRateTypeColumn:
              currentRateTypeString !== '' || versusRateTypeString !== '',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            currentRoomName,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            versusRoomName: ratechange.room_name,
            // Difference row
            showDifferenceRow: true,
            class:
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              ratechange.difference_absolute > 0
                ? 'text-success-default'
                : 'text-critical-default',
            rateDifference: htmlSafe(
              `${formattedAbsoluteDifferenceWithSymbol} / ${formattedRelativeDifference}`,
            ),
            roomChangeText: hasRoomTypeChange
              ? this.intl.t('rates.changes.room_type_change')
              : '',
            // Warning row data
            showWarningRow,
            extractDatetimeDifferenceString,
          };
        }
      }
    }

    return '';
  }

  // Returns the translation for "Rate" or "Average daily rate"
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateTextPlaceholder() {
    // @ts-expect-error: Incorrect type
    return this.args.params?.compareType === 'los'
      ? this.intl.t('rates.average_daily_rate')
      : this.intl.t('rates.rate');
  }

  <template>
    {{! @glint-nocheck }}
    <div
      class="flex items-center gap-100
        {{this.rateChangeValueObj.class}}
        {{@customCss}}"
      role="tooltip"
    >
      {{this.rateChangeValueObj.value}}
      {{#if this.rateChangeValueObj.highestPrioChangeIconName}}
        <div class="cell-rate-changes__prism-icon">
          <PrismIcon
            @name={{this.rateChangeValueObj.highestPrioChangeIconName}}
            @size="extra-small"
          />
        </div>
      {{/if}}
      {{#if this.rateChangeValueObj.value}}
        <EmberTooltip
          @delay={{25}}
          @side="right"
          @tooltipClass="base-tooltip rates-table-tooltip"
        >
          <div class="rates-table-tooltip-container">
            <table cellspacing="0">
              <thead>
                <tr>
                  <th>{{t "general.compare"}}</th>
                  <th>{{this.rateTextPlaceholder}}</th>
                  <th>{{t "rates.changes.roomtype"}}</th>
                  {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                    <th>{{t "rates.type"}}</th>
                  {{/if}}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{this.rateChangeValueObj.currentSelection}}</td>
                  <td>{{this.rateChangeValueObj.currentTooltipRate}}</td>
                  <td>{{this.rateChangeValueObj.currentRoomName}}</td>
                  {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                    <td>{{this.rateChangeValueObj.currentRateTypeString}}</td>
                  {{/if}}
                </tr>
                {{#if this.rateChangeValueObj.showDifferenceRow}}
                  <tr>
                    <td>{{this.compareTextPlaceholder}}</td>
                    <td>
                      <span class={{this.rateChangeValueObj.class}}>
                        {{this.rateChangeValueObj.rateDifference}}
                      </span>
                    </td>
                    <td>{{this.rateChangeValueObj.roomChangeText}}</td>
                    {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                      <td></td>
                    {{/if}}
                  </tr>
                {{/if}}
                <tr>
                  <td>{{this.rateChangeValueObj.versusSelection}}</td>
                  <td>{{this.rateChangeValueObj.versusTooltipRate}}</td>
                  <td>{{this.rateChangeValueObj.versusRoomName}}</td>
                  {{#if this.rateChangeValueObj.hasRateTypeColumn}}
                    <td>{{this.rateChangeValueObj.versusRateTypeString}}</td>
                  {{/if}}
                </tr>
              </tbody>
            </table>
            {{#if this.rateChangeValueObj.showWarningRow}}
              <div class="tooltip-footer flex items-center gap-100">
                <PrismIcon @name="sync-disabled" @size="extra-small" />
                {{this.rateChangeValueObj.extractDatetimeDifferenceString}}
              </div>
            {{/if}}
          </div>
        </EmberTooltip>
      {{/if}}
    </div>
  </template>
}
